/**
 * @file 用户提示弹窗
 * @author liudayu
 */

import { localStorageHelper, STORAGE_KEY } from '@umu/umu-storage';
import { now } from '@umu/umu-util';
import { redirectTo, $location, getQueryString } from '@umu/umu-dom';
import {
    getUnreadDialogs,
    readDialog,
    setStatLogDialogs,
    getAglExpiryReminderPopup,
    markAglExpiryReminderPopupShown,
} from '@umu/umu-api';
import {
    IDailyLogin,
    IDialogData,
    IDialogDataItem,
    IUserLevel,
    IUserMedal,
} from '@umu/umu-api/src/services/api/achievement/getUnreadDialogs';
import { IReadDialogInput } from '@umu/umu-api/src/services/api/achievement/readDialog';
import { IGetAglExpiryReminderPopupOutput } from '@umu/umu-api/src/services/api/achievement/aglExpiryReminder';

import DailyLogin from './components/@dailyLogin/dailyLogin';
import UserLevel from './components/@userLevel/userLevel';
import UserMedal from './components/@userMedal/userMedal';
import { CustomPopup, ICustomPopup } from './components/customPopup/index';
import { LevelPrivilegeOffline } from './components/levelPrivilegeOffline';
import { InviteCUserDialog } from './components/inviteCUserDialog';
import { EnterpriseExpireDialog } from './components/enterpriseExpireDialog';
import { AglExpiryReminderDialog, IAglExpiryReminderData } from './components/aglExpiryReminderDialog';
import { AchivementDialogType } from '@umu/umu-types';
import { getOfficialPopupConfig, IOfficialPopup, OfficialPopup } from './components/officialPopup/index';
import { isFromCourseMiniApp } from '../../../@utils';
import './userAchievementService.scss';
import { getIsProhibitShowPopup, getMockData } from './dataFlow';
import CustomDialog from 'common/src/components/modalDialog/custom';

export const UN_READ_DIALOG_REQUEST_DELAY = 40 * 1000; // 15s

class UserAchievementService {
    /**
     * 设置已读
     */
    public setRead = (objType: AchivementDialogType, objId?: string) => {
        setTimeout(() => {
            const data: IReadDialogInput = {
                obj_type: objType,
            };
            if (objId) {
                data.obj_id = objId;
            }
            readDialog(data);
        }, 0);
    };
    public onTransformDialogData = (data: IDialogData[]) => {
        const dialogList: IDialogData[] = [];
        data.forEach((config: IDialogData) => {
            // data是个数组，按顺序依次展示
            // 首先对数据进行一次处理，保持数据结构扁平
            if (Array.isArray(config.data)) {
                config.data.forEach((medalConfig: IDialogDataItem) => {
                    const item = {
                        type: config.type,
                        data: medalConfig,
                    } as IDialogData;
                    dialogList.push(item);
                });
            } else {
                dialogList.push(config);
            }
        });
        return dialogList;
    };
    public getData = (eid: string) => {
        return new Promise((resolve: (args: IDialogData[]) => void, reject) => {
            const lastUnDialogRequest = Number(localStorageHelper.get(STORAGE_KEY.UN_READ_DIALOG_REQUEST) || 0);
            const unDialogRequestDelay = UN_READ_DIALOG_REQUEST_DELAY; // 15s
            if ($location.href.indexOf('dialogs=1') !== -1) {
                // 测试环境下，url带dialogs=1参数，直接返回mock数据
                resolve(this.onTransformDialogData(getMockData() as IDialogData[]));
            }
            // 15s内不再请求未读弹窗
            if (lastUnDialogRequest && now() - lastUnDialogRequest < unDialogRequestDelay) {
                reject([]);
            } else {
                localStorageHelper.set(STORAGE_KEY.UN_READ_DIALOG_REQUEST, now().toString());
                getUnreadDialogs(
                    eid
                        ? {
                              enterprise_id: eid,
                          }
                        : {},
                ).then((data: IDialogData[]) => {
                    if (!data || data.length === 0 || Object.keys(data || []).length === 0) {
                        reject([]);
                    } else {
                        resolve(this.onTransformDialogData(data));
                    }
                });
            }
        });
    };

    public openUserMedal = (userMedal: IUserMedal) => {
        return new Promise((resolve) => {
            const dialog = CustomDialog({
                className: 'user-achievement-dialog achievement-user-medal-dialog',
                canClose: false,
                childProps: {
                    medalId: userMedal.id,
                    type: userMedal.medal_type,
                    rank: userMedal.medal_rank,
                    medalTitle: userMedal.medal_title,
                    medalRemark: userMedal.medal_remark,
                    points: userMedal.growth_points,
                    medalAction: userMedal.action_name,
                    onClose: () => {
                        dialog.destroy();
                        resolve(void 0);
                    },
                },
                contentComponent: UserMedal,
            });

            this.setRead(AchivementDialogType.USER_MEDAL, userMedal.id);
        });
    };

    public showOfficialPopupDialog = (data: IOfficialPopup) => {
        return new Promise((resolve) => {
            const dialog = CustomDialog({
                className: 'user-achievement-dialog achievement-official-popup-dialog',
                canClose: false,
                childProps: getOfficialPopupConfig(data, {
                    logServer: setStatLogDialogs,
                    callBack: () => {
                        setStatLogDialogs({
                            page_type: Number(data.params.page_type),
                            action_type: data.id,
                        });
                    },
                    onCloseDialog: function () {
                        dialog.destroy();
                        resolve(void 0);
                    },
                }),
                contentComponent: OfficialPopup,
            });
        });
    };
    public openCustomPopup = (popupInfo: ICustomPopup) => {
        return new Promise((resolve) => {
            const dialog = CustomDialog({
                className: 'user-achievement-dialog achievement-custome-popup-dialog',
                canClose: false,
                childProps: {
                    baseInfo: {
                        title: popupInfo.name || '',
                        srcImg: popupInfo.picture || '',
                        link: popupInfo.redirect_url || '',
                    },
                    popupInfo,
                    onClose: () => {
                        setStatLogDialogs({
                            // 15 - 弹框关闭
                            page_type: 15,
                            action_type: popupInfo.id,
                        });
                        dialog.destroy();
                        resolve(void 0);
                    },
                    onImgClick: () => {
                        const redirectUrl = popupInfo.redirect_url || '';
                        if (redirectUrl === '') {
                            return;
                        }
                        redirectTo(popupInfo.redirect_url);
                        setStatLogDialogs({
                            // 14 - 弹框点击
                            page_type: 14,
                            action_type: popupInfo.id,
                        });
                        dialog.destroy();
                        resolve(void 0);
                    },
                },
                contentComponent: CustomPopup,
            });
            setStatLogDialogs({
                page_type: 13, // 13 - 弹框展示
                action_type: popupInfo.id,
            });
        });
    };
    public openDailyLogin = (dailyLogin: IDailyLogin) => {
        return new Promise((resolve) => {
            const dialog = CustomDialog({
                className: 'user-achievement-dialog achievement-daily-login-dialog',
                canClose: false,
                childProps: {
                    activeDays: dailyLogin.active_days,
                    growthPoints: dailyLogin.growth_points,
                    showLoginAchievement: !!dailyLogin.show_login_achievement,
                    onClose: () => {
                        dialog.destroy();
                        resolve(void 0);
                    },
                },
                contentComponent: DailyLogin,
            });

            this.setRead(AchivementDialogType.DAILY_LOGIN);
        });
    };

    public openUpgradeUserLevel = (userLevel: IUserLevel) => {
        return new Promise((resolve) => {
            const dialog = CustomDialog({
                className: 'user-achievement-dialog achievement-user-level-dialog',
                canClose: false,
                childProps: {
                    userLevel: userLevel.user_level,
                    onClose: () => {
                        dialog.destroy();
                        resolve(void 0);
                    },
                },
                contentComponent: UserLevel,
            });

            this.setRead(AchivementDialogType.USER_LEVEL);
        });
    };

    public showLevelPrivilegeOfflineDialog = (data: { offline_date: string }) => {
        return new Promise((resolve) => {
            const dialog = CustomDialog({
                className: 'show-invite-c-user-dialog',
                canClose: false,
                childProps: {
                    data,
                    onClose: () => {
                        dialog.destroy();
                        resolve(void 0);
                    },
                },
                width: '300px',
                contentComponent: LevelPrivilegeOffline,
            });

            this.setRead(AchivementDialogType.LEVEL_PRIVILEGE_OFFLINE);
        });
    };

    public showInviteCUserDialog = (data: any) => {
        return new Promise((resolve) => {
            const dialog = CustomDialog({
                className: 'show-invite-c-user-dialog',
                canClose: false,
                childProps: {
                    data,
                    onClose: () => {
                        dialog.destroy();
                        resolve(void 0);
                    },
                },
                width: '300px',
                contentComponent: InviteCUserDialog,
            });
        });
    };
    public showEnterpriseExpire = (data: any) => {
        return new Promise((resolve) => {
            const dialog = CustomDialog({
                className: 'show-invite-c-user-dialog',
                canClose: false,
                childProps: {
                    data,
                    onClose: () => {
                        this.setRead(AchivementDialogType.ENTERPRISE_EXPIRE, '');
                        dialog.destroy();
                        resolve(void 0);
                    },
                },
                width: '300px',
                contentComponent: EnterpriseExpireDialog,
            });
        });
    };
    public showAglExpiryReminderDialog = (data: IAglExpiryReminderData) => {
        return new Promise((resolve) => {
            const dialog = CustomDialog({
                className: 'user-achievement-dialog agl-expiry-reminder-dialog-wrap',
                canClose: false,
                childProps: {
                    data,
                    onClose: () => {
                        dialog.destroy();
                        resolve(void 0);
                    },
                    onShown: () => {
                        markAglExpiryReminderPopupShown();
                    },
                },
                width: '400px',
                contentComponent: AglExpiryReminderDialog,
            });
        });
    };

    /**
     * AGL 服务到期提醒弹窗，独立于成就弹窗队列，与 §6.1/§6.2 接口成对使用
     */
    public checkAglExpiryReminder = () => {
        getAglExpiryReminderPopup().then((res: IGetAglExpiryReminderPopupOutput) => {
            if (res && res.should_popup && res.popup_type !== null) {
                this.showAglExpiryReminderDialog({
                    popup_type: res.popup_type,
                    end_date_str: res.end_date_str,
                });
            }
        });
    };

    public openDialog = (list: IDialogData[]) => {
        const dialogFuncMap: { [index: string]: (args: any) => Promise<unknown> } = {
            [AchivementDialogType.USER_LEVEL]: this.openUpgradeUserLevel,
            [AchivementDialogType.DAILY_LOGIN]: this.openDailyLogin,
            [AchivementDialogType.USER_MEDAL]: this.openUserMedal,
            [AchivementDialogType.POPUP_CUSTOM]: this.openCustomPopup,
            [AchivementDialogType.INVITE_ENTERPRISE]: this.showInviteCUserDialog,
            [AchivementDialogType.ENTERPRISE_EXPIRE]: this.showEnterpriseExpire,
            user_level_privilege_offline: this.showLevelPrivilegeOfflineDialog,
            [AchivementDialogType.OFFICIAL_POPUP]: this.showOfficialPopupDialog,
        };
        function nextDialog() {
            const config = list.shift();
            if (!config) {
                return;
            }
            if (isFromCourseMiniApp()) {
                return;
            }
            const data = config.data;
            const type = config.type;
            if (!data) {
                nextDialog();
                return;
            }
            const func = dialogFuncMap[type];
            if (func) {
                func(data).then(() => {
                    nextDialog();
                });
            }
        }
        nextDialog();
    };

    public startup = () => {
        const eid = getQueryString('eid') || '';
        // 禁止弹窗标识
        if (getIsProhibitShowPopup(eid)) {
            return;
        }
        this.checkAglExpiryReminder();
        this.getData(eid).then(
            (data: IDialogData[]) => {
                this.openDialog(data);
            },
            () => {
                // console.log('data error');
            },
        );
    };
}

const userAchievementService = new UserAchievementService();

export default userAchievementService;
